import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';

import { reportDuration } from '../report-duration';
import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportView } from '../report.model';

/**
 * Page de garde du tirage papier.
 *
 * Invisible à l'écran (`.print-cover` est en `display: none` hors impression) :
 * elle n'existe que pour ouvrir le PDF sur une feuille d'identification, et
 * elle est posée **hors** de `.print-sheet` pour que les bandeaux courants de
 * ce tableau ne l'atteignent pas. D'où aussi son `aria-hidden` : tout ce
 * qu'elle annonce est répété par l'en-tête, et un lecteur d'écran n'a pas à
 * l'entendre deux fois.
 *
 * **Une seule mention de scolarité.** La maquette imprimait « niveau · classe »
 * — deux valeurs de démonstration. Le modèle n'en porte qu'une, le parcours
 * (`learningPathTitle`), qui tient lieu de classe : on rend donc la seule
 * fente correspondante et on retire l'autre **avec** son séparateur `·`, plutôt
 * que d'imprimer deux fois le même texte de part et d'autre d'un point médian.
 *
 * Hôte en `display: contents` : la couverture est un frère direct de
 * `.print-sheet` dans l'enveloppe du rapport, et un élément de composant
 * intercalé s'insérerait entre les deux — la règle `break-after: page` porte
 * sur la section, pas sur une boîte qui l'envelopperait.
 */
@Component({
  selector: 's2e-report-print-cover',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe],
  templateUrl: './report-print-cover.component.html',
  host: { '[style.display]': "'contents'" },
})
export class ReportPrintCoverComponent {
  readonly view = input.required<ReportView>();

  /** Durée de la séance, dans l'unité qui lui convient. Voir {@link reportDuration}. */
  protected readonly duration = computed(() =>
    reportDuration(this.view().header.durationMinutes),
  );
}
